import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Download,
  Filter,
  Search,
  ChevronDown,
  CalendarRange,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { getProformas, deleteProforma } from '../../../../services/finance_services/proformaService';
import { exportProformasListExcel } from '../../../../services/export_services/proformaExportService';
import useApi from '../../../../hooks/useApi';
import Message from '../../../../components/Message';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { formatDate } from '../../../../utils/date';
import ReportTable from '../../../../components/ReportTable';
import Pagination from '../../../../components/Pagination';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'converted', label: 'Converted' }
];

const ProformasPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    dateRange: '',
    startDate: '',
    endDate: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proformaToDelete, setProformaToDelete] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Use the useApi hook
  const { 
    data: proformasData, 
    loading, 
    error, 
    callApi: fetchProformas 
  } = useApi(getProformas ,{showSuccessToast: false});

  const { 
    loading: deleteLoading, 
    error: deleteError, 
    callApi: callDeleteProforma 
  } = useApi(deleteProforma, {showSuccessToast: true, msg: 'Proforma deleted'});

  useEffect(() => {
    loadProformas();
  }, [pagination.page, pagination.page_size]);

  const loadProformas = async () => {
    const apiFilters = {
      search: searchTerm,
      status: filters.status,
      date_range: filters.dateRange,
      start_date: filters.startDate,
      end_date: filters.endDate,
    };
    
    const data = await fetchProformas(pagination.page, pagination.page_size, apiFilters);
    
    if (data) {
      setPagination(prev => ({
        ...prev,
        total_pages: data.total_pages || 0,
        count: data.count || 0
      }));
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleDeleteClick = (proforma) => {
    setProformaToDelete(proforma);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!proformaToDelete) return;
    
    await callDeleteProforma(proformaToDelete.id);
    
    if (!deleteError) {
      // Reload proformas after successful deletion
      await loadProformas();
      setShowDeleteModal(false);
      setProformaToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProformaToDelete(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProformas();
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      dateRange: '',
      startDate: '',
      endDate: '',
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    // Reload with cleared filters
    setTimeout(() => loadProformas(), 0);
  };

  const handleExportProformas = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const exportFilters = {
        start_date: filters.startDate,
        end_date: filters.endDate,
      };
      
      const result = await exportProformasListExcel(exportFilters);
      if (result.success) {
        // Success message is handled by the download
      } else {
        setExportError(result.error || 'Failed to export proformas to Excel.');
      }
    } catch (err) {
      setExportError(err.message || 'Failed to export proformas to Excel.');
    } finally {
      setExporting(false);
    }
  };

  const proformas = proformasData?.results || [];

  const columns = [
    { label: 'Proforma Number', key: 'proforma_number' },
    { label: 'Title', key: 'title' },
    { label: 'Client', key: 'client' },
    { label: 'Amount', key: 'amount' },
    { label: 'Status', key: 'status_display' },
    { label: 'Date Issued', key: 'date_issued', format: formatDate },
    { label: 'Expiry Date', key: 'expiry_date', format: formatDate },
    {
      key: 'actions',
      render: (row) => (
        <div className="flex gap-2">
          <button
            className="text-blue-600 hover:bg-blue-100 p-1 rounded"
            onClick={() => navigate(`/dashboard/proformas/${row.id}`)}
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            className="text-yellow-600 hover:bg-yellow-100 p-1 rounded"
            onClick={() => navigate(`/dashboard/proformas/edit/${row.id}`)}
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            className="text-red-600 hover:bg-red-100 p-1 rounded"
            onClick={() => handleDeleteClick(row)}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proforma Reports</h1>
          <p className="text-gray-600">Manage and track your proformas</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <button 
            onClick={() => navigate('/dashboard/proformas/add')} 
            className="inline-flex items-center px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Proforma
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Search proformas..." 
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  applyFilters();
                }
              }}
            />
          </div>
          {/* Filter, Reset, Export */}
          <div className="flex gap-2">
            <button 
              onClick={applyFilters}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
            >
              Apply Filters
            </button>
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2 text-gray-500" />
              <span>Filters</span>
              <ChevronDown className={`h-4 w-4 ml-2 text-gray-500 transition-transform ${showFilters ? 'transform rotate-180' : ''}`} />
            </button>
            <button 
              onClick={resetFilters} 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            <button 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center text-gray-700 hover:bg-gray-50"
              onClick={handleExportProformas}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4 mr-2 text-gray-500" />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>
        </div>
        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="status"
                name="status"
                className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.status}
                onChange={handleFilterChange}
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            
            {/* Date Range filter */}
            <div>
              <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CalendarRange className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  id="dateRange"
                  name="dateRange"
                  className="w-full pl-10 border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.dateRange}
                  onChange={handleFilterChange}
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
            </div>
            
            {/* Custom date range inputs */}
            {filters.dateRange === 'custom' && (
              <>
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Error Messages */}
      <Message type="error" message={error || deleteError} />
      <Message type="error" message={exportError} />
      
      {/* Proforma Table */}
      <ReportTable 
        columns={columns} 
        data={proformas} 
        loading={loading || deleteLoading}
        emptyMessage="No proformas found. Try adjusting your search or filter criteria."
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.total_pages}
        totalItems={pagination.count}
        pageSize={pagination.page_size}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        itemName="Proformas"
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Proforma"
        message={proformaToDelete ? `Are you sure you want to delete "${proformaToDelete.title}"?` : ''}
      />
    </div>
  );
};

export default ProformasPage;